// The gallery is its own module so gallery-only sections (featured product, accessory media,
// quick view) load it without the purchase form and cart code.
const [{ cart, newIdentity }] = await Promise.all([
  import(globalThis.MeriAssets?.cart || './cart.js'),
  import(globalThis.MeriAssets?.productGallery || './product-gallery.js'),
]);

/** Keep Shopify's language/market path and preview context on product requests. */
export function productAddress(productUrl, currentHref, localeRoot = '/') {
  const current = new URL(currentHref);
  const target = new URL(productUrl, current);
  if (target.origin !== current.origin) throw new Error('Product URL must use the current storefront.');
  const prefix = new URL(localeRoot, current).pathname.replace(/\/?$/, '/');
  if (prefix !== '/' && !target.pathname.startsWith(prefix))
    target.pathname = prefix + target.pathname.replace(/^\//, '');
  const parameters = new URLSearchParams(current.search);
  // A previous variant belongs to the old selection, including combined listings.
  for (const key of ['variant', 'option_values', 'selling_plan', 'section_id', 'sections']) parameters.delete(key);
  for (const [key, value] of target.searchParams) parameters.set(key, value);
  target.search = parameters.toString();
  target.hash = '';
  return target;
}

export function variantRequestUrl(
  productUrl,
  currentHref,
  localeRoot,
  sectionId,
  values,
  sellingPlan = '',
  variantId = '',
) {
  const url = productAddress(productUrl, currentHref, localeRoot);
  for (const key of ['variant', 'option_values', 'section_id', 'sections']) url.searchParams.delete(key);
  url.searchParams.set('section_id', sectionId);
  if (values.length) url.searchParams.set('option_values', values.join(','));
  else if (variantId) url.searchParams.set('variant', variantId);
  if (sellingPlan) url.searchParams.set('selling_plan', sellingPlan);
  else url.searchParams.delete('selling_plan');
  return url;
}

/** Update native constraints and plan choices without replacing input nodes. */
export function syncNativePurchaseControls(form, replacement) {
  const quantity = form.elements.namedItem('quantity');
  const nextQuantity = replacement?.elements.namedItem('quantity');
  if (quantity && nextQuantity)
    for (const name of ['min', 'max', 'step']) {
      const value = nextQuantity.getAttribute(name);
      if (value === null) quantity.removeAttribute(name);
      else quantity.setAttribute(name, value);
    }
  const plan = form.elements.namedItem('selling_plan');
  const nextPlan = replacement?.elements.namedItem('selling_plan');
  let invalidated = false;
  if (plan && nextPlan) {
    const previous = plan.value,
      selected = nextPlan.value;
    const options = [...nextPlan.options];
    invalidated = Boolean(previous && !options.some((option) => option.value === previous));
    plan.replaceChildren(...options);
    plan.required = nextPlan.required;
    plan.disabled = nextPlan.disabled;
    plan.value = selected;
    if (invalidated) plan.selectedIndex = -1;
    plan.setCustomValidity(invalidated ? plan.dataset.unavailableMessage : '');
  }
  return invalidated;
}

/** Theme Block drops expose @theme, not a commerce type. Use the rendered
 * direct children, which also exclude blocks hidden by the theme editor. */
export function normalizeProductBlocks(scope) {
  for (const block of scope.querySelectorAll('[data-product-block]')) {
    const details = block.parentElement;
    const kind = block.dataset.productBlock;
    const placed = details?.matches('[data-product-details]') && details.closest('[data-meri-product]');
    const first = placed && [...details.children].find((node) => node.dataset.productBlock === kind);
    if (first === block) continue;
    // Keep Shopify's editor wrapper and the Liquid explanations. Invalid
    // content must not take part in selectors, variant state or submissions.
    block.querySelector('[data-product-block-content]')?.remove();
    block.removeAttribute('data-product-price');
    block.removeAttribute('data-product-variants');
  }
}

export class ProductController {
  constructor(root) {
    this.root = root;
    normalizeProductBlocks(root);
    this.abort = new AbortController();
    this.generation = 0;
    this.mutationId = newIdentity();
    this.form = root.querySelector('[data-meri-product-form]');
    if (!this.form) return;
    this.message = root.querySelector('[data-product-message]');
    this.variantPreviews = new Map();
    this.inventoryPreviews = new Map();
    this.readVariantPreviews(root);
    const state = root.querySelector('[data-variant-state]');
    this.available = Boolean(
      this.variantInput?.value &&
      (state ? state.dataset.available === 'true' : !this.form.querySelector('[data-add-button]')?.disabled),
    );
    root.addEventListener(
      'input',
      (event) => {
        if (event.target.form === this.form && event.target.name === 'quantity') this.syncPurchaseState();
      },
      { signal: this.abort.signal },
    );
    root.addEventListener(
      'change',
      (event) => {
        if (event.target.form === this.form && event.target.name === 'selling_plan') {
          event.target.setCustomValidity('');
          this.updateVariant();
          return;
        }
        if (!event.target.matches('[data-option-value]')) return;
        const target = event.target.dataset.productUrl;
        if (target) {
          const context = [location.href, globalThis.Shopify?.routes?.root || '/'];
          const destination = productAddress(target, ...context);
          if (destination.pathname !== productAddress(root.dataset.productUrl, ...context).pathname) {
            location.assign(destination.href);
            return;
          }
        }
        this.updateVariant();
      },
      { signal: this.abort.signal },
    );
    root
      .querySelector('[data-variant-retry]')
      ?.addEventListener('click', () => this.updateVariant(), { signal: this.abort.signal });
    root.addEventListener('meri:options:ready', () => this.syncPurchaseState(), { signal: this.abort.signal });
    this.form.addEventListener('submit', (event) => this.submit(event), { signal: this.abort.signal });
    // Liquid stays readable if a module fails. Unlock purchases only after all
    // handlers exist, so native submission cannot silently buy an old variant.
    this.ready = true;
    this.syncPurchaseState();
    if ([...root.querySelectorAll('[data-option-value]')].some((input) => input.checked !== input.defaultChecked))
      this.updateVariant();
  }
  get variantInput() {
    return this.form?.elements.namedItem('id');
  }
  get active() {
    return !this.destroyed && this.root.isConnected;
  }
  readVariantPreviews(scope) {
    for (const [selector, cache] of [
      ['template[data-variant-preview]', this.variantPreviews],
      ['template[data-inventory-preview]', this.inventoryPreviews],
    ]) {
      for (const template of scope.querySelectorAll(selector)) {
        const key = template.dataset.options + (template.dataset.inventoryBlockId || '');
        cache.set(key, template);
        if (cache.size > 128) cache.delete(cache.keys().next().value);
      }
    }
  }
  previewVariant() {
    const selected = [...this.root.querySelectorAll('[data-option-value]:checked')].map(
      (input) => input.dataset.optionLabel,
    );
    const key = JSON.stringify(selected);
    const plan = this.form.elements.namedItem('selling_plan')?.value;
    const preview = !plan && selected.every((value) => value !== undefined) && this.variantPreviews.get(key);
    const price = this.root.querySelector('meri-promotion-price');
    const nextPrice = preview?.content?.querySelector('meri-promotion-price');
    if (price && nextPrice) price.replaceWith(nextPrice.cloneNode(true));
    else price?.setAttribute('data-pending', '');
    if (preview) {
      this.variantInput.value = preview.dataset.variantId;
      this.available = preview.dataset.available === 'true';
      for (const key of ['variantId', 'available', 'mediaId']) this.variantInput.dataset[key] = preview.dataset[key];
    }
    for (const inventory of this.root.querySelectorAll('[data-product-inventory]')) {
      const next = preview && this.inventoryPreviews.get(key + inventory.dataset.inventoryBlockId);
      inventory.hidden = !next;
      if (next) inventory.querySelector('[data-inventory-content]')?.replaceChildren(next.content.cloneNode(true));
    }
  }
  text(key, fallback) {
    return globalThis.MeriStrings?.[key] || fallback;
  }
  /** [data-product-price] is replaced wholesale, so assistive tech would never hear the
   * change; the stable live region beside it is written only after a confirmed update. */
  announceVariant() {
    const status = this.root.querySelector('[data-product-price-status]');
    if (!status) return;
    const price = this.root
      .querySelector('[data-product-price] [data-price-current]')
      ?.textContent.replace(/\s+/g, ' ');
    const parts = [];
    // Without a variant the price shown belongs to Shopify's fallback, not the selection.
    if (this.variantInput?.value && price?.trim()) parts.push(price.trim());
    if (!this.available) parts.push(this.text('unavailable', 'Unavailable'));
    status.textContent = parts.join(', ');
  }
  syncPurchaseState() {
    const button = this.form.querySelector('[data-add-button]');
    const optionsPending = Boolean(this.root.querySelector('meri-options[data-options-pending]'));
    const blocked =
      !this.ready || optionsPending || this.busy || this.pending || !this.available || !this.variantInput?.value;
    if (this.variantInput) this.variantInput.disabled = !this.ready || optionsPending || this.pending;
    const unready = this.form.querySelector('[data-purchase-unready]');
    if (unready) unready.hidden = Boolean(this.ready && !optionsPending);
    for (const fieldset of this.root.querySelectorAll('[data-product-option-fields]')) fieldset.disabled = !this.ready;
    if (button) {
      button.disabled = Boolean(blocked);
      const text = this.busy
        ? this.text('adding', 'Adding…')
        : this.available
          ? this.text('addToCart', 'Add to cart')
          : this.text('unavailable', 'Unavailable');
      // 2026-11-04 (Owner): the button's label (product-buy.liquid) rolls into place when it changes (base.css
      // data-roll); the first sync only sets it. A button without one takes the text itself.
      const label = this.form.querySelector('[data-add-label]') || button;
      if (label.textContent !== text) {
        label.textContent = text;
        if (this.labelShown && label !== button) label.dataset.roll = label.dataset.roll === 'a' ? 'b' : 'a';
      }
      this.labelShown = true;
    }
    // B separately hides unsafe options through its scoped CSS. This does not
    // remove B's data-options-cart-only state or certify a payment method.
    const accelerated = this.form.querySelector('[data-accelerated-checkout]');
    const nativeValid = ['quantity', 'selling_plan'].every(
      (name) => this.form.elements.namedItem(name)?.validity?.valid !== false,
    );
    if (accelerated) {
      // Keep Shopify's wallet mounted and visible during a transient variant
      // request, but never let it submit the previous variant or accessories.
      accelerated.hidden = Boolean(
        !this.ready || optionsPending || !this.available || !this.variantInput?.value || !nativeValid,
      );
      accelerated.inert = Boolean(accelerated.hidden || this.pending || this.busy);
      accelerated.toggleAttribute('data-updating', Boolean(!accelerated.hidden && (this.pending || this.busy)));
    }
    if (this.busy || this.pending) this.form.setAttribute('aria-busy', 'true');
    else this.form.removeAttribute('aria-busy');
  }
  async updateVariant() {
    if (!this.active || !this.form) return;
    const generation = ++this.generation;
    this.requestAbort?.abort();
    clearTimeout(this.requestTimer);
    const controller = new AbortController();
    this.requestAbort = controller;
    const timer = (this.requestTimer = setTimeout(() => controller.abort(), 12000));
    const standardSelection = globalThis.MeriStandardEvents?.product(
      this.root,
      [...this.root.querySelectorAll('[data-option-value]:checked')].map((input) => ({
        name: input.dataset.optionName,
        value: input.dataset.optionLabel,
      })),
    );
    const current = () => this.active && generation === this.generation;
    this.pending = true;
    this.previewVariant();
    this.syncPurchaseState();
    for (const hint of this.root.querySelectorAll('[data-pickup-availability], [data-product-payment-terms]'))
      hint.hidden = true;
    const focused = document.activeElement;
    const restoreFocus = focused?.matches('[data-option-value]') && this.root.contains(focused);
    try {
      const url = variantRequestUrl(
        this.root.dataset.productUrl,
        location.href,
        globalThis.Shopify?.routes?.root || '/',
        this.root.dataset.sectionId,
        [...this.root.querySelectorAll('[data-option-value]:checked')].map((input) => input.value),
        this.form.elements.namedItem('selling_plan')?.value || '',
        this.variantInput.value || this.variantInput.dataset.variantId,
      );
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('variant request');
      const html = await response.text();
      if (!current()) return;
      if (controller.signal.aborted) throw new DOMException('Variant request timed out', 'AbortError');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = [...parsed.querySelectorAll('[data-meri-product]')].find(
        (root) =>
          root.dataset.sectionId === this.root.dataset.sectionId &&
          root.dataset.productId === this.root.dataset.productId,
      );
      if (replacement) normalizeProductBlocks(replacement);
      const state = replacement?.querySelector('[data-variant-state]');
      if (!state) throw new Error('missing variant state');
      this.readVariantPreviews(replacement);
      const resolvedStandardVariant = globalThis.MeriStandardEvents?.variant(replacement);
      const patches = ['[data-product-variants]', '[data-product-price]'].map((selector) => ({
        existing: this.root.querySelector(selector),
        next: replacement.querySelector(selector),
      }));
      if (patches.some(({ existing, next }) => existing && !next)) throw new Error('incomplete variant section');
      const shouldFocus = restoreFocus && document.activeElement === focused;
      for (const { existing, next } of patches) if (existing && next) existing.replaceWith(next);
      // The gallery stays, so its SALE badge takes the chosen variant's compare-at sale from the new render.
      const sale = 'meri-gallery .product-badges meri-promotion-badge';
      this.root
        .querySelector(sale)
        ?.toggleAttribute(
          'data-compare-sale',
          Boolean(replacement.querySelector(sale)?.hasAttribute('data-compare-sale')),
        );
      const pickup = this.root.querySelector('[data-pickup-availability]');
      const nextPickup = replacement.querySelector('[data-pickup-availability]');
      if (pickup && nextPickup) pickup.replaceWith(nextPickup);
      else if (pickup) pickup.replaceChildren();
      const inventory = [...replacement.querySelectorAll('[data-product-inventory]')];
      for (const existing of this.root.querySelectorAll('[data-product-inventory]')) {
        const next = inventory.find((node) => node.dataset.inventoryBlockId === existing.dataset.inventoryBlockId);
        if (next) existing.replaceWith(next);
        else {
          existing.replaceChildren();
          existing.hidden = true;
        }
      }
      // Only variant/price fragments change. The original form and File nodes stay.
      const planInvalidated = syncNativePurchaseControls(
        this.form,
        replacement.querySelector('[data-meri-product-form]'),
      );
      this.variantInput.value = state.dataset.variantId || '';
      const liveState = this.root.querySelector('[data-variant-state]');
      if (liveState) Object.assign(liveState.dataset, state.dataset);
      this.available = state.dataset.available === 'true' && Boolean(this.variantInput.value);
      if (this.variantInput.value && !resolvedStandardVariant)
        standardSelection?.reject(new Error('Official variant event data unavailable'));
      else standardSelection?.resolve({ variant: this.variantInput.value ? resolvedStandardVariant : null });
      if (this.message)
        this.message.textContent = planInvalidated
          ? this.form.elements.namedItem('selling_plan').dataset.unavailableMessage
          : '';
      const retry = this.root.querySelector('[data-variant-retry]');
      if (retry) retry.hidden = true;
      // An optional media enhancement cannot invalidate a confirmed purchase.
      try {
        const gallery = this.root.querySelector('meri-gallery');
        if (state.dataset.mediaId && gallery?.showMedia(state.dataset.mediaId) === false) {
          const nextGallery = replacement.querySelector('meri-gallery');
          // The server places the selected variant first, even outside the
          // configured media limit. Replace only the gallery, keeping inputs.
          if (
            [...(nextGallery?.querySelectorAll('[data-media-id]') || [])].some(
              (slide) => slide.dataset.mediaId === state.dataset.mediaId,
            )
          )
            gallery.replaceWith(nextGallery);
        }
      } catch {
        /* Native media remains usable. */
      }
      this.form.dispatchEvent(
        new CustomEvent('meri:variant:change', {
          bubbles: true,
          detail: { variantId: this.variantInput.value, available: this.available },
        }),
      );
      if (!current()) return;
      // Options synchronously reconciles shopify-additional-items here. Only
      // then can the existing native wallet accept the new variant.
      this.pending = false;
      this.syncPurchaseState();
      this.announceVariant();
      // Setting .value fires no event. Shopify's accelerated checkout element reads
      // the product form, so announce the confirmed variant as a native change,
      // as Shopify's reference theme does; theme listeners ignore the id input.
      if (this.variantInput.value) this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      // Shopify renders the replacement fieldsets disabled until the form is ready;
      // focus can only land after syncPurchaseState has enabled them.
      if (shouldFocus)
        [...this.root.querySelectorAll('[data-option-value]')]
          .find((input) => input.value === focused.value && input.name === focused.name)
          ?.focus({ preventScroll: true });
      const address = new URL(location.href);
      address.searchParams.delete('option_values');
      if (this.variantInput.value) address.searchParams.set('variant', this.variantInput.value);
      else address.searchParams.delete('variant');
      const selectedPlan = this.form.elements.namedItem('selling_plan')?.value;
      if (selectedPlan) address.searchParams.set('selling_plan', selectedPlan);
      else address.searchParams.delete('selling_plan');
      try {
        history.replaceState(history.state, '', address);
      } catch {
        /* A history restriction does not change Shopify's result. */
      }
    } catch (error) {
      standardSelection?.reject(error);
      if (!current()) return;
      this.pending = false;
      this.available = false;
      this.variantInput.value = '';
      this.syncPurchaseState();
      // previewVariant hid the price while waiting; a failed check must not leave it invisible.
      this.root.querySelector('meri-promotion-price[data-pending]')?.removeAttribute('data-pending');
      if (this.message)
        this.message.textContent = this.text('variantError', 'This option could not be checked. Please retry.');
      const retry = this.root.querySelector('[data-variant-retry]');
      if (retry) retry.hidden = false;
      // Keep recoverable B inputs on transport failure; no confirmed variant changed.
    } finally {
      standardSelection?.reject(new DOMException('Selection superseded or detached', 'AbortError'));
      clearTimeout(timer);
      if (this.requestAbort === controller) {
        this.requestAbort = null;
        this.requestTimer = null;
      }
    }
  }
  async submit(event) {
    event.preventDefault();
    if (
      !this.active ||
      this.busy ||
      this.pending ||
      !this.available ||
      !this.variantInput?.value ||
      this.root.querySelector('meri-options[data-options-pending]') ||
      !this.form.reportValidity()
    )
      return;
    // Set the guard before prepare: a handler must not re-enter a second submit.
    this.busy = true;
    this.syncPurchaseState();
    let standardAdd;
    let adding;
    try {
      const detail = { form: this.form, additionalItems: [], errors: [] };
      const allowed = this.form.dispatchEvent(
        new CustomEvent('meri:product:prepare', { detail, bubbles: true, cancelable: true }),
      );
      if (!allowed || detail.errors.length) {
        if (this.message && detail.errors.length) this.message.textContent = detail.errors.join(' ');
        return;
      }
      if (!this.active || this.pending || !this.available || !this.variantInput.value) return;
      if (this.message) this.message.textContent = this.text('adding', 'Adding…');
      // Validation passed: let the cart drawer open at once with what this page already
      // shows, while the real add and Shopify's pricing continue in the background.
      // A drawer that takes over supplies its rendering context for the bundled response.
      adding = {
        form: this.form,
        preview: this.cartPreview(detail.additionalItems),
        renderingContext: undefined,
      };
      this.form.dispatchEvent(new CustomEvent('meri:product:adding', { bubbles: true, detail: adding }));
      standardAdd = globalThis.MeriStandardEvents?.cart(this.root, 'add', 'product', [
        {
          merchandiseId: String(this.variantInput.value),
          quantity: Number(this.form.elements.namedItem('quantity')?.value || 1),
        },
        ...detail.additionalItems.map((item) => ({
          merchandiseId: String(item.id),
          quantity: Number(item.quantity || 1),
        })),
      ]);
      const updatedCart = await cart.addForm(this.form, {
        additionalItems: detail.additionalItems,
        mutationId: this.mutationId,
        renderingContext: adding.renderingContext,
      });
      standardAdd?.resolve(updatedCart);
      if (!this.active) {
        // The drawer can survive a product section replacement while this write finishes.
        adding.settle?.({ cart: updatedCart });
        return;
      }
      // Submission owns both the button and its live status. The drawer only
      // presents the cart and must not own purchase-state cleanup.
      this.clearAddingMessage();
      const navigateToCart = this.form.dispatchEvent(
        new CustomEvent('meri:product:added', {
          bubbles: true,
          cancelable: true,
          detail: { form: this.form, cart: updatedCart },
        }),
      );
      if (!this.active) return;
      this.mutationId = newIdentity();
      if (navigateToCart) location.assign((globalThis.Shopify?.routes?.root || '/') + 'cart');
    } catch (error) {
      standardAdd?.reject(error);
      const message =
        error.code === 'rejected'
          ? error.message
          : this.text('cartError', 'We could not confirm the update. Review your cart before trying again.');
      if (!this.active) {
        adding?.settle?.({ message });
        return;
      }
      if (this.message) this.message.textContent = message;
      // An open drawer may be showing this add in advance; it must withdraw it.
      this.form.dispatchEvent(
        new CustomEvent('meri:product:add-failed', { bubbles: true, detail: { form: this.form, message } }),
      );
    } finally {
      this.busy = false;
      this.clearAddingMessage();
      if (this.active) this.syncPurchaseState();
    }
  }
  // What the cart will show, taken only from Shopify output already on this page:
  // title, selected options, the loaded gallery image, the displayed (campaign) price
  // and each selected add-on's Shopify price. Nothing here is calculated.
  cartPreview(additionalItems = []) {
    try {
      return this.readCartPreview(additionalItems);
    } catch {
      return null; // An optional preview must never block the real add.
    }
  }
  readCartPreview(additionalItems) {
    const shown = (node) => node?.textContent.replace(/\s+/g, ' ').trim() || '';
    const visible = (properties) =>
      Object.entries(properties || {}).filter(([name, value]) => !name.startsWith('_') && String(value).trim());
    const mediaId = this.variantInput.dataset.mediaId;
    const image =
      (/^\d+$/.test(mediaId || '') && this.root.querySelector(`[data-media-id="${mediaId}"] img`)) ||
      this.root.querySelector('meri-gallery img');
    const price = this.root.querySelector('[data-product-price]');
    // The add-on items about to be submitted carry the same visible property and
    // required flag the cart will show; the page supplies their names, images and prices.
    const selects = [...this.form.elements].filter(
      (control) => control.matches?.('[data-options-addon-variant]') && !control.disabled && control.value,
    );
    const addons = additionalItems.map((item) => {
      const index = selects.findIndex((control) => control.value === String(item.id));
      const select = index < 0 ? undefined : selects.splice(index, 1)[0];
      const value = select?.closest('[data-options-value]');
      const option = select?.selectedOptions[0];
      return {
        title: select?.closest('[data-options-addon]')?.dataset.optionsLabel || '',
        // Matches the cart line: option names with their values.
        variant: option?.dataset.variantOptions || option?.dataset.variantTitle || '',
        image: value?.querySelector('img.options-item-image')?.currentSrc || '',
        // A struck-through compare-at price (2.9.9) sits beside the price; only the price itself is read.
        price:
          option?.dataset.price === '0'
            ? shown(
                value?.querySelector('[data-options-item-current]') ||
                  value?.querySelector('[data-options-item-price]'),
              )
            : option?.dataset.priceLabel || '',
        original: option?.dataset.compareLabel || '',
        properties: visible(item.properties),
        required: item.properties?._meri_required === '1',
        quantity: String(item.quantity || 1),
      };
    });
    // Visible line properties, as the cart lists them (names starting with "_" stay hidden).
    const properties = [...new FormData(this.form)]
      .map(([name, value]) => [/^properties\[([^\]]+)\]$/.exec(name)?.[1], value])
      .filter(([name, value]) => name && !name.startsWith('_') && typeof value === 'string' && value.trim());
    const plan = this.form.elements.namedItem('selling_plan');
    return {
      title: shown(this.root.querySelector('h1')),
      url: location.pathname,
      variant: [...this.root.querySelectorAll('[data-option-value]:checked')]
        .map((input) => input.dataset.optionName + ': ' + input.dataset.optionLabel)
        .join(' / '),
      plan: plan?.value ? shown(plan.selectedOptions?.[0]) : '',
      properties,
      image: image?.currentSrc || image?.src || '',
      price: shown(price?.querySelector('[data-price-current]')),
      original: shown(price?.querySelector('[data-price-original]:not([hidden]) [data-price-original-value]')),
      // A running campaign means Shopify will list a discount line under this product.
      discounted: Boolean(price?.querySelector('[data-promotion-active]')),
      quantity: this.form.elements.namedItem('quantity')?.value || '1',
      addons,
    };
  }
  clearAddingMessage() {
    if (this.message?.textContent === this.text('adding', 'Adding…')) this.message.textContent = '';
  }
  destroy() {
    this.destroyed = true;
    this.ready = false;
    this.busy = false;
    this.pending = false;
    this.clearAddingMessage();
    if (this.form) this.syncPurchaseState();
    ++this.generation;
    this.abort.abort();
    this.requestAbort?.abort();
    clearTimeout(this.requestTimer);
    this.variantPreviews?.clear();
    this.inventoryPreviews?.clear();
  }
}

const controllers = new Map();
function mount(scope = document) {
  normalizeProductBlocks(scope);
  const roots = [...scope.querySelectorAll('[data-meri-product]')];
  if (scope.matches?.('[data-meri-product]')) roots.unshift(scope);
  for (const root of roots) if (!controllers.has(root)) controllers.set(root, new ProductController(root));
}
if (typeof document !== 'undefined') {
  mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [root, instance] of controllers)
      if (event.target.contains(root)) {
        instance.destroy();
        controllers.delete(root);
      }
  });
}
